import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api-service/api.service';
import { APIEndpoints } from '@core/config/apiendpoint';
import { Subject, Subscription, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private optionsData: any = {};
  private nonEmployeeReqState: any;
  public personData: any;
  isRoleHR: boolean = false;
  locationDataSubscription!: Subscription;
  areaDataSubscription!: Subscription;
  departmentDataSubscription!: Subscription;
  superVisorDataSubscription!: Subscription;
  stateDataSubscription!: Subscription;
  programDataSubscription!: Subscription;
  employeeTypeDataSubscription!: Subscription;
  applicationDataSubscription!: Subscription;
  // Observable string sources
  private countChangeSource = new Subject<string>();
  private requestChangeSource = new Subject<string>();
  private accessChangeSource = new Subject<string>();
  // Observable string streams
  countChangeItem$ = this.countChangeSource.asObservable();
  requestChangeItem$ = this.requestChangeSource.asObservable();
  accessChangeItem$ = this.accessChangeSource.asObservable();

  constructor(private apiService: ApiService) { }


  getOptionsData() {
    return this.optionsData;
  }

  setRoleHRExists(value: any) {
    this.isRoleHR = value;
  }

  getRoleHRExists() {
    return this.isRoleHR;
  }

  // Service message commands
  countChangeItemHandler(countChangeItem: any) {
    this.countChangeSource.next(countChangeItem);
  }

  // Service message commands
  requestChangeItemHandler(requestChangeItem: any) {
    this.requestChangeSource.next(requestChangeItem);
  }

  // Service message commands
  accessChangeItemHandler(accessChangeItem: any) {
    this.accessChangeSource.next(accessChangeItem);
  }

  getLocationData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getLocationData, requestParam).pipe(map((res) => res));
  }

  getAreaData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getAreaData, requestParam).pipe(map((res) => res));
  }

  getDepartmentData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getDepartmentData, requestParam).pipe(map((res) => res));
  }

  getSuperVisorData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getSuperVisorList, requestParam).pipe(map((res) => res));
  }

  getStateData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getStateList, requestParam).pipe(map((res) => res));
  }

  getProgramData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getProgramList, requestParam).pipe(map((res) => res));
  }

  getEmployeeTypeData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getEmployeeTypeList, requestParam).pipe(map((res) => res));
  }

  getApplicationData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getAppList, requestParam).pipe(map((res) => res));
  }

  getRequestData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getRequestDetails, requestParam).pipe(map((res) => res));
  }

  getDepartmentHistoryData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getDepartmentDetails, requestParam).pipe(map((res) => res));
  }

  getManagerData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getManagerDetails, requestParam).pipe(map((res) => res));
  }

  getJobDetailData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getJobDetails, requestParam).pipe(map((res) => res));
  }

  getSystemAccessData(requestParam: any) {
    return this.apiService.get(APIEndpoints.getSystemAccessDetails, requestParam).pipe(map((res) => res));
  }

  getSystemAccessCompleteHistory(requestParam: any) {
    return this.apiService.get(APIEndpoints.getSystemAccessCompleteHistory, requestParam).pipe(map((res) => res));
  }

  getCounts(requestParam: any) {
    return this.apiService.get(APIEndpoints.getCountList, requestParam).pipe(map((res) => res));
  }

  submitSystemAccessRequest(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.submitSystemAccessRequest, data, requestParam).pipe(map((res) => res));
  }

  getMyStaffList(requestParam: any) {
    return this.apiService.get(APIEndpoints.getMyStaffList, requestParam).pipe(map((res) => res));
  }

  getStaffData(requestParam: any) {
    return this.apiService.get(APIEndpoints.staffNameListData, requestParam).pipe(map((res) => res));
  }

  getMyDepartmentList(requestParam: any) {
    return this.apiService.get(APIEndpoints.getMyDepartmentList, requestParam).pipe(map((res) => res));
  }

  getPersonalDetails(requestParam: any) {
    return this.apiService.get(APIEndpoints.getPersonDetails, requestParam).pipe(map((res) => res));
  }

  getUpdateProof(requestParam: any) {
    return this.apiService.get(APIEndpoints.downloadUpdateProof, requestParam).pipe(map((res) => res));
  }

  updateManagerApproval(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateManagerDetails, data, requestParam).pipe(map((res) => res));
  }
  updateProgramApproval(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateProgramDetails, data, requestParam).pipe(map((res) => res));
  }
  updateJobApproval(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateJobDetails, data, requestParam).pipe(map((res) => res));
  }

  updateContactInformation(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateContactDetails, data, requestParam).pipe(map((res) => res));
  }

  updateLastNameInformation(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateLastNameDetails, data, requestParam).pipe(map((res) => res));
  }

  updateDateApproval(data: any, requestParam: any) {
    return this.apiService.post(APIEndpoints.updateDateDetails, data, requestParam).pipe(map((res) => res));
  }

  async getCommonOptionData() {
    this.locationDataSubscription = await this.getLocationData('').subscribe((data: any) => {
      this.optionsData['locationData'] = data;
    });
    this.areaDataSubscription = await this.getAreaData('').subscribe((data: any) => {
      this.optionsData['areaData'] = data;
    });
    this.departmentDataSubscription = await this.getDepartmentData('').subscribe((data: any) => {
      this.optionsData['departmentData'] = data;
    });
    this.superVisorDataSubscription = await this.getSuperVisorData('').subscribe((data: any) => {
      this.optionsData['superVisorData'] = data;
    });
    this.stateDataSubscription = await this.getStateData('').subscribe((data: any) => {
      this.optionsData['stateData'] = data;
    });
    this.programDataSubscription = await this.getProgramData('').subscribe((data: any) => {
      this.optionsData['programData'] = data;
    });
    this.employeeTypeDataSubscription = await this.getEmployeeTypeData('').subscribe((data: any) => {
      this.optionsData['employeeTypeData'] = data;
    });

    let appRequestParams = {
      personId: 0,
      requestType: 100
    }
    this.applicationDataSubscription = await this.getApplicationData(appRequestParams).subscribe((data: any) => {
      this.optionsData['appList'] = data;
    });
  }

  setNonEmployeeReqState(state: any) {
    this.nonEmployeeReqState = state;
  }

  getNonEmployeeReqState() {
    return this.nonEmployeeReqState;
  }

  setPersonData(data: any) {
    this.personData = data;
  }

  getPersonData() {
    return this.personData;
  }
}
